import { Modal } from '@kep-ui-kit/ui-components';
import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { StyledButton, StyledFormGroup, StyledJsonContainer, StyledErrorLabel } from '@selfserve/react-ui';
import {api, apiEndpoints} from "@selfserve/react-utils";
import {usePost} from "@selfserve/react-hooks";

const StyledExtendModal = styled(Modal)`
  > .kepuikit-modal-container > .MuiPaper-root {
    background-color: transparent !important;
    box-shadow: none !important;
    width: 50%;
    max-width: 1200px;
    height: 70%;
    justify-content: center;
    overflow: hidden;
  }
`;

const StyledExtendModalContent = styled('div')`
  display: flex;
  flex-direction: column;
  padding: 24px;
  background-color: #fff;
  border-radius: 4px;
  overflow: auto;
  height: 100%;
  .kepuikit-base-input-root {
    height: 40px !important;
  }
`;

const StyledHeaderRow = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const StyledButtonContainer = styled('div')`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  button:first-of-type {
    margin-right: 16px;
  }
`;

const jsonContainerInModalClass = `
  width: 100% !important;
  height: calc(100% - 60px) !important;
  box-sizing: border-box;
  resize: none;
  white-space: pre-wrap;
`;

interface Props {
  open: boolean;
  checklistJson: any;
  handleClose: () => void;
  refetch: () => void;
  checklistId: string; // Add this prop to set the title
}

export const AdminChecklistExtendModal = ({
                                            checklistJson,
                                            handleClose,
                                            open,
                                            refetch,
                                            checklistId,
                                          }: Props) => {
  const { t } = useTranslation();
  const { openErrorModal } = useOutletContext<any>() || {};
  const [jsonValue, setJsonValue] = useState<string>(JSON.stringify(checklistJson, null, 2));
  const [jsonError, setJsonError] = useState<string | null>(null);
  const { mutate: createChecklist } = usePost(apiEndpoints.checklistCreate);
  const { mutate: updateChecklist } = usePost(apiEndpoints.checklistUpdate.replace(':id', checklistId));

  useEffect(() => {
    if (open) {
      setJsonValue(JSON.stringify(checklistJson, null, 2));
      setJsonError(null);
    }
  }, [open, checklistJson]);

  const handleJsonChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setJsonValue(value);
    try {
      JSON.parse(value);
      setJsonError(null);
    } catch (e) {
      setJsonError('Invalid JSON format');
    }
  }, []);

  const handleClone = useCallback(() => {
    try {
      const parsedJson = JSON.parse(jsonValue);
      const uniqueId = new Date().toLocaleString().slice(0, 19).replace('T', ' ');
      const clonedJson = {
        ...parsedJson,
        name: `${parsedJson.name || 'Checklist'} Clone ${uniqueId}`,
        status: 'disable'
      };
      // remove fields that should not be cloned
      ['_id', 'updated_at', 'created_at', '__v'].forEach((key) => delete clonedJson[key]);
      createChecklist(clonedJson, {
        onSuccess: () => {
          handleClose();
          refetch();
        },
        onError: (e: unknown) => {
          openErrorModal({ text: e instanceof Error ? e.message : 'Unknown error occurred' });
        }
      });
    } catch (e: any) {
      setJsonError('Invalid JSON format');
      openErrorModal({ text: e.message });
    }
  }, [jsonValue, handleClose, refetch, createChecklist, openErrorModal]);

  const handleSave = useCallback(() => {
    try {
      const parsedJson = JSON.parse(jsonValue);
      updateChecklist(parsedJson, {
        onSuccess: () => {
          handleClose();
          refetch();
        },
        onError: (e: unknown) => {
          if (e instanceof Error) {
            setJsonError('Invalid JSON format');
            openErrorModal({ text: e.message });
          } else {
            openErrorModal({ text: 'An unknown error occurred' });
          }
        },
      });
    } catch (e: unknown) {
      if (e instanceof Error) {
        setJsonError('Invalid JSON format');
        openErrorModal({ text: e.message });
      } else {
        openErrorModal({ text: 'An unknown error occurred' });
      }
    }
  }, [jsonValue, handleClose, refetch, updateChecklist, openErrorModal]);

  return (
    <StyledExtendModal
      backdropColor="rgba(0,110,250,.8)"
      onClose={handleClose}
      open={open}
      fullWidth
      aria-labelledby="modal-title"
    >
      <StyledExtendModalContent>
        <StyledHeaderRow>
          <h2 id="modal-title" style={{ margin: 0 }}>{checklistId}</h2>
          <StyledButton isBorderless withoutMargin onClick={handleClone}>
            {t('common.clone')}
          </StyledButton>
        </StyledHeaderRow>
        <div style={{ marginBottom: 12 }}>{t('Checklist json')}</div>
        <StyledFormGroup smallLabel smallMargin bold style={{ flex: 1 }}>
          <StyledJsonContainer
            value={jsonValue}
            onChange={handleJsonChange}
            error={!!jsonError}
            modalMode
          />
          {jsonError && <StyledErrorLabel>{jsonError}</StyledErrorLabel>}
        </StyledFormGroup>
        <StyledButtonContainer>
          <StyledButton isBorderless withoutMargin onClick={handleClose}>
            {t('common.cancel')}
          </StyledButton>
          <StyledButton withoutMargin onClick={handleSave}>
            {t('common.save')}
          </StyledButton>
        </StyledButtonContainer>
      </StyledExtendModalContent>
    </StyledExtendModal>
  );
};

const style = document.createElement('style');
style.textContent = `
  .json-container-modal {
    ${jsonContainerInModalClass}
  }
`;
document.head.append(style);
